<template>
  <v-list
    nav
    density="comfortable"
    class="py-3"
  >
    <template v-for="item in sidebarNavigation" :key="item.id">
      <!-- Single item without children -->
      <template v-if="!item.children || item.children.length === 0">
        <v-list-item
          v-if="item.route"
          :to="item.route"
          :active="isActiveRoute(item.route)"
          color="primary"
          rounded="md"
        >
          <template v-slot:prepend>
            <SimpleIcon v-if="isSimpleIcon(item.icon)" :name="getSimpleIconName(item.icon)" :size="24" color="#9E9E9E" />
            <v-icon v-else-if="convertIcon(item.icon)" color="#9E9E9E">{{ convertIcon(item.icon) }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
        
        <v-list-item
          v-else
          :active="item.active"
          color="primary"
          rounded="md"
        >
          <template v-slot:prepend>
            <SimpleIcon v-if="isSimpleIcon(item.icon)" :name="getSimpleIconName(item.icon)" :size="24" color="#9E9E9E" />
            <v-icon v-else-if="convertIcon(item.icon)" color="#9E9E9E">{{ convertIcon(item.icon) }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <!-- Item with children (expandable) -->
      <v-list-group
        v-else
        :value="item.id"
      >
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            :active="isActiveSection(item)"
            color="primary"
            rounded="md"
          >
            <template v-slot:prepend>
              <SimpleIcon v-if="isSimpleIcon(item.icon)" :name="getSimpleIconName(item.icon)" :size="24" color="#9E9E9E" />
              <v-icon v-else-if="convertIcon(item.icon)" color="#9E9E9E">{{ convertIcon(item.icon) }}</v-icon>
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>

        <!-- Children items -->
        <v-list-item
          v-for="child in item.children"
          :key="child.id"
          :to="child.route"
          :active="isActiveRoute(child.route)"
          color="primary"
          rounded="md"
          class="ml-4"
        >
          <template v-slot:prepend>
            <SimpleIcon v-if="isSimpleIcon(child.icon)" :name="getSimpleIconName(child.icon)" :size="20" color="#9E9E9E" />
            <v-icon v-else-if="convertIcon(child.icon)" size="small" color="#9E9E9E">{{ convertIcon(child.icon) }}</v-icon>
          </template>
          <v-list-item-title>{{ child.title }}</v-list-item-title>
        </v-list-item>
      </v-list-group>
    </template>
  </v-list>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SimpleIcon from './SimpleIcon.vue';

export default {
  name: 'NavigationMenu',
  components: {
    SimpleIcon
  },
  computed: {
    ...mapGetters('navigation', ['sidebarNavigation', 'isSectionOpen']),
    currentRoute() {
      return this.$route.path;
    }
  },
  methods: {
    ...mapActions('navigation', ['toggleSection', 'setSectionState']),
    
    isActiveRoute(route) {
      return this.currentRoute === route;
    },
    
    isActiveSection(item) {
      // Check if any child route is active
      if (item.children && item.children.length > 0) {
        return item.children.some(child => this.isActiveRoute(child.route));
      }
      return false;
    },
    
    ensureActiveSectionOpen() {
      // Auto-open section if it contains active route
      this.sidebarNavigation.forEach(item => {
        if (item.children && item.children.length > 0) {
          const hasActiveChild = item.children.some(child => this.isActiveRoute(child.route));
          if (hasActiveChild && !this.isSectionOpen(item.id)) {
            this.setSectionState({ sectionId: item.id, isOpen: true });
          }
        }
      });
    },
    
    isSimpleIcon(bootstrapIcon) {
      return bootstrapIcon === 'bi bi-telegram';
    },
    
    getSimpleIconName(bootstrapIcon) {
      const simpleIconMap = {
        'bi bi-telegram': 'telegram'
      };
      return simpleIconMap[bootstrapIcon];
    },
    
    convertIcon(bootstrapIcon) {
      // Pass-through for MDI icons — if the config already specifies an `mdi-*`
      // icon, use it directly instead of requiring an entry in iconMap.
      if (typeof bootstrapIcon === 'string' && bootstrapIcon.startsWith('mdi-')) {
        return bootstrapIcon;
      }
      // Map Bootstrap icons to Material Design Icons
      const iconMap = {
        'bi bi-house-door': 'mdi-home',
        'bi bi-house': 'mdi-home',
        'bi bi-people': 'mdi-account-group',
        'bi bi-person': 'mdi-account',
        'bi bi-gear': 'mdi-cog',
        'bi bi-cog': 'mdi-cog',
        'bi bi-shield': 'mdi-shield',
        'bi bi-shield-lock': 'mdi-shield-lock',
        'bi bi-folder': 'mdi-folder',
        'bi bi-file-text': 'mdi-file-document',
        'bi bi-chat': 'mdi-message',
        'bi bi-bell': 'mdi-bell',
        'bi bi-graph-up': 'mdi-chart-line',
        'bi bi-list': 'mdi-format-list-bulleted',
        'bi bi-clipboard': 'mdi-clipboard-text',
        'bi bi-box': 'mdi-package-variant',
        'bi bi-tag': 'mdi-tag',
        'bi bi-tags': 'mdi-tag-multiple',
        'bi bi-search': 'mdi-magnify',
        'bi bi-filter': 'mdi-filter',
        'bi bi-plus': 'mdi-plus',
        'bi bi-pencil': 'mdi-pencil',
        'bi bi-trash': 'mdi-delete',
        'bi bi-eye': 'mdi-eye',
        'bi bi-download': 'mdi-download',
        'bi bi-upload': 'mdi-upload',
        'bi bi-chevron-right': 'mdi-chevron-right',
        'bi bi-chevron-down': 'mdi-chevron-down',
        'bi bi-chevron-left': 'mdi-chevron-left',
        'bi bi-chevron-up': 'mdi-chevron-up',
        // New icons
        'bi bi-activity': 'mdi-monitor-dashboard',
        'bi bi-speedometer2': 'mdi-monitor-dashboard',
        'bi bi-plus-square': 'mdi-plus-box',
        'bi bi-list-ul': 'mdi-format-list-bulleted-square',
        'bi bi-bar-chart': 'mdi-chart-bar',
        'bi bi-bar-chart-line': 'mdi-chart-line',
        'bi bi-chat-left-text': 'mdi-message-text',
        'bi bi-database': 'mdi-database',
        'bi bi-collection': 'mdi-database',
        'bi bi-globe': 'mdi-web',
        'mdi-bookshelf': 'mdi-bookshelf',
        'mdi-fire': 'mdi-fire',
        'mdi-tag-multiple-outline': 'mdi-tag-multiple-outline',
        'mdi-file-chart': 'mdi-file-chart',
      };
      
      return iconMap[bootstrapIcon] || 'mdi-circle-outline';
    }
  },
  watch: {
    // Watch for navigation changes and ensure active section is open
    sidebarNavigation: {
      handler() {
        this.$nextTick(() => {
          this.ensureActiveSectionOpen();
        });
      },
      immediate: true
    },
    
    currentRoute() {
      this.ensureActiveSectionOpen();
    }
  }
};
</script>

<style scoped>
/* Vuetify overrides for better appearance */
.v-list {
  padding: 8px;
}

.v-list-item {
  margin-bottom: 2px;
}

/* Child items indentation */
.ml-4 {
  margin-left: 16px !important;
}

/* Ensure proper spacing in rail mode */
.v-navigation-drawer--rail .v-list-item {
  padding-inline: 8px !important;
}
</style>